/* Audit Assistant — input box.
 * Auto-growing textarea. Enter = send, Shift+Enter = newline. Send is
 * disabled when empty or while the assistant is typing. Optional char
 * counter near the cap. */

import { useEffect, useRef } from 'react';
import type { JSX, KeyboardEvent } from 'react';

const MAX_CHARS = 1000;

interface ChatInputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean; // true while typing
  placeholder?: string;
}

export function ChatInput({ value, onChange, onSend, disabled, placeholder }: ChatInputProps): JSX.Element {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow to fit content (capped by CSS max-height).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [value]);

  const canSend = value.trim().length > 0 && !disabled;

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <div className="aa-input">
      <textarea
        ref={ref}
        className="aa-input-field"
        value={value}
        maxLength={MAX_CHARS}
        rows={1}
        placeholder={placeholder ?? 'Ask Audit Assistant…'}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Message Audit Assistant"
      />
      <div className="aa-input-row">
        <span className="aa-counter">
          {value.length > MAX_CHARS * 0.8 ? `${value.length}/${MAX_CHARS}` : ''}
        </span>
        <button
          type="button"
          className="aa-send"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send message"
          data-tip="Send (Enter)"
        >
          ↑
        </button>
      </div>
    </div>
  );
}
